import {
  AppliedPromoDetail,
  firstChargeDate,
  getAllCountries,
  getStatesByCode2,
  inputStyles,
  nextDueDate,
} from '@/components/CheckoutSubscriptionPlan.tsx'
import LogoutButton from '@/components/LogoutButton'
import CheckoutMethods from '@/components/custom-components/CheckoutMethods'
import { Loader } from '@/components/custom-components/Loader'
import OnboardingHeader from '@/components/onboarding/OnboardingHeader.tsx'
import { Badge } from '@/components/shadcn/badge.tsx'
import { Button } from '@/components/shadcn/button'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input.tsx'
import { Label } from '@/components/shadcn/label.tsx'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/shadcn/select.tsx'
import { Tabs, TabsList, TabsTrigger } from '@/components/shadcn/tabs.tsx'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/shadcn/tooltip'
import { useCheckout } from '@/hooks/useCheckout'
import { handleCheckoutStart } from '@/lib/gtag'
import { cn } from '@/lib/utils.ts'
import { featureFlagKeys } from '@/utils/data/featureFlags'
import { DROP_SHIPPER, PlanDetail, SPARK_PLAN, VISION_PASS, getCurrencySymbol, plans } from '@/utils/data/plans.ts'
import { trpc } from '@/utils/trpc.ts'
import { CardCVV, CardComponent, CardExpiry, CardNumber } from '@chargebee/chargebee-js-react-wrapper'
import ChargebeeComponents from '@chargebee/chargebee-js-react-wrapper/dist/components/ComponentGroup'
import { zodResolver } from '@hookform/resolvers/zod'
import { Lock, XCircle } from '@mynaui/icons-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { TRPCError } from '@trpc/server'
import { ArrowLeftIcon } from 'lucide-react'
import { useFeatureFlagEnabled } from 'posthog-js/react'
import { SyntheticEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { useLocalStorage } from 'usehooks-ts'
import { z } from 'zod'

function getDiscountedPrice(originalPrice: string, appliedPromo?: AppliedPromoDetail): string {
  if (!appliedPromo) return originalPrice

  // Remove currency symbol and convert to number
  const price = parseFloat(originalPrice.replace(/[^0-9.-]+/g, ''))

  let discountedPrice: number

  if (appliedPromo.discountType === 'percentage') {
    // Calculate percentage discount
    discountedPrice = price * (1 - (appliedPromo.discountPercentage ?? 0) / 100)
  } else {
    // Calculate fixed amount discount
    discountedPrice = price - (appliedPromo.discountAmount ? appliedPromo.discountAmount / 100 : 0)
  }

  // Format back to currency string with 2 decimal places
  return `$${Math.max(0, discountedPrice).toFixed(2)}`
}

type SearchParams = {
  plan?: string
  redirect?: string
}

export const Route = createFileRoute('/checkout')({
  component: Checkout,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const plan = search?.plan as string | undefined

    return {
      plan,
    }
  },
})

function Checkout() {
  const { redirect } = Route.useSearch()

  const annualFlagEnabled = useFeatureFlagEnabled(featureFlagKeys.annualPlans)
  const extraPaymentMethodsEnabled = useFeatureFlagEnabled(featureFlagKeys.extraPaymentMethods)

  const [cosPlan, set_cos_plan] = useLocalStorage<string>('cos_plan', '')
  const [cosCoupon, setCoupon] = useLocalStorage<string | undefined>('cos_coupon', undefined)

  const [period, setPeriod] = useState<'monthly' | 'annually'>('monthly')

  const isLifetimePlan = cosPlan === VISION_PASS.plan_id || cosPlan === DROP_SHIPPER.plan_id

  const isSparkPlan = cosPlan === SPARK_PLAN.plan_id || cosPlan === SPARK_PLAN.no_trial_plan_id

  // Ref to ensure applyPromo is only called once
  const hasAppliedPromo = useRef(false)

  const cardRef = useRef<ChargebeeComponents | null>(null)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [addPromoOpen, setAddPromoOpen] = useState<boolean>(false)
  const [selectedPlan, setSelectedPlan] = useState<PlanDetail | undefined>(undefined)
  const [appliedCoupon, setAppliedCoupon] = useState<string | undefined>()
  const [couponRemoveLoading, setCouponRemoveLoading] = useState<boolean>(false)
  const [appliedPromo, setAppliedPromo] = useState<AppliedPromoDetail | undefined>(undefined)

  const navigate = useNavigate()

  const countries = getAllCountries()

  const { checkoutSubscription, isSubscribed, isSubscribing } = useCheckout()

  const checkoutFormSchema = z.object({
    chargebeeToken: z.string({ required_error: 'Chargebee Token is required' }),
    coupon: z.string(),
    firstName: z.string({ required_error: 'First name is required' }).min(2, 'First name is required'),
    lastName: z.string({ required_error: 'Last name is required' }).min(2, 'Last name is required'),
    plan: z.string({ required_error: 'Plan ID is required' }),
    billingAddress: z.object({
      country: z
        .string({ required_error: 'Country is required' })
        .describe('country in ISO Code https://www.iso.org/iso-3166-country-codes.html')
        .min(2, 'Country is required'),
      zip: z.string({ required_error: 'Zip code is required' }).min(2, 'Zip code is required'),
      city: z.string({ required_error: 'City is required' }).min(2, 'City is required'),
      state: z.string({ required_error: 'State/Region is required' }).min(2, 'State/Region is required'),
      line1: z.string({ required_error: 'Line 1 is required' }).min(2, 'Line 1 is required'),
      line2: z.string().optional(),
    }),
  })

  type CheckoutFormValues = z.infer<typeof checkoutFormSchema>

  const defaultValues: Partial<CheckoutFormValues> = {
    coupon: cosCoupon || '',
    chargebeeToken: '',
    plan: '',
    firstName: '',
    lastName: '',
    billingAddress: {
      city: '',
      zip: '',
      state: '',
      line1: '',
      line2: '',
      country: '',
    },
  }

  const form = useForm<CheckoutFormValues>({
    resolver: zodResolver(checkoutFormSchema),
    defaultValues,
  })

  useLayoutEffect(() => {
    document.body.classList.toggle('dark', false)
  }, [])

  useEffect(() => {
    setCoupon(appliedCoupon)
  }, [appliedCoupon])

  useEffect(() => {
    if (cosCoupon && !appliedCoupon) {
      setAppliedCoupon(cosCoupon)
    }
  }, [cosCoupon])

  useEffect(() => {
    if (!cosPlan || !plans) {
      navigate({ to: '/plans', search: { redirect } })

      return
    }

    const selected = [...plans, DROP_SHIPPER, VISION_PASS].find(
      (p) => p.plan_id === cosPlan || p.yearly_plan_id === cosPlan,
    )

    if (selected) {
      setSelectedPlan(selected)
      handleCheckoutStart([
        {
          item_id: cosPlan,
          item_name: selected.name,
          price: parseInt(selected.price.replace('$', ''), 10),
          quantity: 1,
        },
      ])
    }
  }, [cosPlan, plans, navigate, handleCheckoutStart])

  // Set period based on the `cos_plan` in localStorage
  useEffect(() => {
    const setInitialPeriod = () => {
      const currentPlan = plans.find((plan) => plan.plan_id === cosPlan || plan.yearly_plan_id === cosPlan)

      if (currentPlan) {
        setPeriod('monthly')
      }
    }

    setInitialPeriod()
  }, [cosPlan])

  // Update localStorage when period changes
  const updateLocalStorage = (newPeriod: 'monthly' | 'annually') => {
    const cosPlanKey = newPeriod === 'monthly' ? 'plan_id' : 'yearly_plan_id'
    const cosPlan = plans.find((plan) => plan[cosPlanKey])

    if (cosPlan) {
      set_cos_plan(cosPlan[cosPlanKey as keyof typeof cosPlan].toString())
    }
  }

  const handlePeriodChange = (newPeriod: 'monthly' | 'annually') => {
    setPeriod(newPeriod)
    updateLocalStorage(newPeriod)
  }

  useEffect(() => {
    const handleStorageChange = () => {
      const updatedCosPlan = cosPlan
      const matchingPlan = plans.find(
        (plan) => plan.plan_id === updatedCosPlan || plan.yearly_plan_id === updatedCosPlan,
      )
      if (matchingPlan) {
        setPeriod(updatedCosPlan === matchingPlan.yearly_plan_id ? 'annually' : 'monthly')
      }
    }

    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])
  useEffect(() => {
    if (cosCoupon && !hasAppliedPromo.current) {
      form.setValue('coupon', cosCoupon)

      applyPromo()
      hasAppliedPromo.current = true
    }
  }, [cosCoupon])

  const subscribeToPlan = () => {
    if (cardRef.current) {
      cardRef.current
        .tokenize({
          addressLine1: form.getValues('billingAddress.line1'),
          addressLine2: form.getValues('billingAddress.line2'),
          city: form.getValues('billingAddress.city'),
          state: form.getValues('billingAddress.state'),
          zip: form.getValues('billingAddress.zip'),
          countryCode: form.getValues('billingAddress.country'),
          firstName: form.getValues('firstName'),
          lastName: form.getValues('lastName'),
        })
        .then((data: { token: string }) => {
          form.setValue('chargebeeToken', data.token)
        })
        .then(() => {
          setTimeout(async () => {
            try {
              const plan = cosPlan || (!annualFlagEnabled && form.getValues('plan'))

              checkoutSubscription({
                plans: plan ? [plan] : [],
                coupons: form.getValues('coupon')
                  ? ([form.getValues('coupon')].filter(Boolean) as string[])
                  : undefined,
                chargebeeToken: form.getValues('chargebeeToken'),
                billingAddress: form.getValues('billingAddress'),
              })
            } catch (e) {
              setIsSubmitting(false)
              if (e instanceof Error) {
                if (e.message.includes('already present')) {
                  toast.error('This account already exists.')
                  if (redirect?.toString().includes('/')) {
                    window.location.href = redirect
                  } else {
                    navigate({ to: '/feeds/templates' })
                  }
                } else {
                  toast.error(e.message) // Show the original error for other cases
                }
              } else {
                toast.error('An unknown error occurred')
              }
            } finally {
              setIsSubmitting(false)
            }
          }, 500)
        })
        .catch((e: TRPCError) => {
          setIsSubmitting(false)
          toast.error(e.message)
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    } else {
      setIsSubmitting(false)
    }
  }

  const onSubmit = () => {
    setIsSubmitting(true)

    if (form.getValues('coupon') && form.getValues('coupon')?.length > 0 && !appliedPromo) {
      try {
        return refetchCoupon({ throwOnError: true })
          .then(() => {
            subscribeToPlan()
          })
          .catch((e) => {
            if (e instanceof Error) {
              if (e.message === "Sorry, we couldn't find that resource") toast.error('This coupon code is invalid!')
              else toast.error(e.message)
            } else {
              toast.error('An unknown error occurred')
            }
          })
      } catch (e) {
        setIsSubmitting(false)
        if (e instanceof Error) {
          if (e.message === "Sorry, we couldn't find that resource") toast.error('This coupon code is invalid!')
          else toast.error(e.message)
        } else {
          toast.error('An unknown error occurred')
        }
      } finally {
        setIsSubmitting(false)
      }
    } else {
      subscribeToPlan()
    }
  }

  const applyPromo = async () => {
    try {
      const response = await refetchCoupon({ throwOnError: true })
      if (response && response.data) {
        setAddPromoOpen(false)
        setAppliedPromo(response.data)
        setAppliedCoupon(form.getValues('coupon'))
      }
    } catch (e) {
      if (e instanceof Error) {
        if (e.message === "Sorry, we couldn't find that resource") {
          form.setValue('coupon', '')
          toast.error('This coupon code is invalid!')
        } else toast.error(e.message)
      } else {
        toast.error('An unknown error occurred')
      }
    }
  }

  const onReady = (event: SyntheticEvent) => {
    const element = event.currentTarget as HTMLElement
    if (element) element.focus()
  }

  const { isLoading: isLoadingChargebeeSync, isRefetching: isRefetchingChargebeeSync } = trpc.getIsUserSynced.useQuery(
    undefined,
    {
      enabled: isSubscribed,
    },
  )

  useEffect(() => {
    if (!isSubscribed) return

    setIsSubmitting(false)

    if (redirect?.toString().includes('/')) {
      window.location.href = redirect
    } else {
      window.location.href = '/feeds/templates'
    }
  }, [isSubscribed])

  const {
    isLoading: isLoadingCoupon,
    isRefetching: isRefetchingCoupon,
    refetch: refetchCoupon,
  } = trpc.getCouponDetails.useQuery(
    {
      couponId: !form.getFieldState('coupon').invalid ? form.getValues('coupon') : '',
    },
    { enabled: false, retry: false },
  )

  function getSubmitButtonText({
    isSubmitting,
    isSubscribing,
    isLoadingChargebeeSync,
    isRefetchingChargebeeSync,
    isSparkPlan,
    isLifetimePlan,
  }: {
    isSubmitting: boolean
    isSubscribing: boolean
    isLoadingChargebeeSync: boolean
    isRefetchingChargebeeSync: boolean
    isSparkPlan: boolean
    isLifetimePlan: boolean
  }): string {
    if (isSubmitting || isSubscribing || isLoadingChargebeeSync || isRefetchingChargebeeSync) {
      return 'Processing...'
    }

    if (isSparkPlan) {
      return 'Activate Spark'
    }

    if (isLifetimePlan) {
      return 'Get Lifetime Access'
    }

    return 'Start Trial'
  }

  function getCheckoutFooterNote({
    isLifetimePlan,
    isSparkPlan,
    selectedPlan,
    firstChargeDate,
    nextDueDate,
  }: {
    isLifetimePlan: boolean
    isSparkPlan: boolean
    selectedPlan: { price: string }
    firstChargeDate: string
    nextDueDate: string
  }): string {
    if (isLifetimePlan) {
      return `You’ll be charged a one-time fee of ${selectedPlan.price} today, ${firstChargeDate}. No future charges.`
    }

    if (isSparkPlan) {
      return `Your subscription will begin today, ${firstChargeDate}, and you’ll be charged ${selectedPlan.price}/month. You may cancel at any time.`
    }

    return `After your free trial ends, you will be charged ${selectedPlan.price} per month starting ${nextDueDate}. You can cancel your subscription before your free trial ends.`
  }

  function getFormattedPromoDetails(appliedPromo: AppliedPromoDetail) {
    if (!appliedPromo) return ''

    const currencySymbol =
      appliedPromo.discountType === 'fixed_amount' && appliedPromo.currencyCode
        ? getCurrencySymbol(appliedPromo.currencyCode)
        : ''

    const discountValue = appliedPromo.discountAmount
      ? `${(appliedPromo.discountAmount / 100).toFixed(2)}`
      : `${appliedPromo.discountPercentage}%`

    const duration =
      appliedPromo.durationType === 'limited_period' ? `/ ${appliedPromo.periodUnit}` : appliedPromo.durationType

    return `${currencySymbol}${discountValue} ${duration}`
  }

  function getPromoDescription(appliedPromo: AppliedPromoDetail) {
    if (!appliedPromo) return ''

    const currencySymbol =
      appliedPromo.discountType === 'fixed_amount' && appliedPromo.currencyCode
        ? getCurrencySymbol(appliedPromo.currencyCode)
        : ''

    const discountValue = appliedPromo.discountAmount
      ? `${(appliedPromo.discountAmount / 100).toFixed(2)}`
      : `${appliedPromo.discountPercentage}%`

    const offLabel = ' OFF'

    const billingFrequency =
      appliedPromo.periodUnit === 'month' ? 'monthly' : appliedPromo.periodUnit === 'year' ? 'yearly' : ''

    const priceLabel = ' Price '

    const duration = appliedPromo.periodUnit
      ? `for ${appliedPromo.period} ${appliedPromo.periodUnit}`
      : appliedPromo.durationType

    return `${currencySymbol}${discountValue}${offLabel} ${billingFrequency}${priceLabel}${duration}`
  }

  const isCouponLoading = useMemo(() => {
    return isLoadingCoupon || isRefetchingCoupon || couponRemoveLoading
  }, [isLoadingCoupon, isRefetchingCoupon, appliedCoupon, couponRemoveLoading])

  return (
    <div className={'bg-brandgrad bg-no-repeat bg-center bg-cover min-h-screen'}>
      <div className="flex justify-between items-center gap-2">
        <OnboardingHeader />
        <div className="pr-5 lg:pr-10 pt-5 lg:pt-10">
          <LogoutButton />
        </div>
      </div>
      <div className={'flex-1 flex flex-col justify-center items-center py-[7.75rem]'}>
        {selectedPlan && (
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={`bg-themebackground dark:bg-themebackgrounddark rounded-lg p-5 w-11/12 ${annualFlagEnabled ? 'lg:max-w-2xl' : 'lg:max-w-lg'} border `}
            >
              <div className={'flex flex-col gap-6'}>
                <div className={'flex flex-col gap-3'}>
                  <Button
                    onClick={() =>
                      navigate({
                        to: '/plans',
                      })
                    }
                    variant={'ghost'}
                    className={'flex  items-center justify-start m-0 p-0 w-fit align-middle'}
                  >
                    <ArrowLeftIcon size={'1.25rem'} />
                    <span>Back</span>
                  </Button>
                  <div className={'flex justify-between items-center gap-2'}>
                    <h4 className={'text-xl lg:text-2xl text-left font-semibold'}>Subscribe to CreativeOS</h4>
                    {isLifetimePlan || isSparkPlan ? null : (
                      <Badge variant={'secondaryDestructive'} className={'text-nowrap'}>
                        3-Day Trial
                      </Badge>
                    )}
                  </div>
                </div>
                <div>
                  <div className={'flex flex-col md:flex-row justify-between gap-3 items-center'}>
                    <p className={'font-bold'}>Subscription</p>
                    {annualFlagEnabled ? (
                      <Tabs defaultValue={period}>
                        <TabsList className={'rounded-full'}>
                          <TabsTrigger
                            className={'rounded-full'}
                            value={'monthly'}
                            onClick={() => handlePeriodChange('monthly')}
                          >
                            Pay Monthly
                          </TabsTrigger>
                          <TabsTrigger
                            className={'rounded-full flex gap-1.5'}
                            value={'annually'}
                            onClick={() => handlePeriodChange('annually')}
                          >
                            <span>Pay Yearly</span> <span className={'text-themedestructive'}>Save 50%</span>
                          </TabsTrigger>
                        </TabsList>
                      </Tabs>
                    ) : null}
                  </div>
                  <div className={'flex flex-col'}>
                    <div className={'flex flex-col gap-3 py-3 border-b border-dashed'}>
                      <div>
                        <div className={'flex gap-3 justify-between text-sm'}>
                          <span className={'flex gap-1 items-center'}>
                            <img src={'/images/icon_no_bg.png'} width={16} height={16} alt={''} />
                            <span>CreativeOS {selectedPlan.name}</span>
                          </span>
                          <span className={'font-semibold'}>
                            {period === 'monthly' ? selectedPlan.price : selectedPlan.annual_price}{' '}
                            {isLifetimePlan ? '- One time' : <span>/ {period === 'monthly' ? 'month' : 'year'}</span>}
                          </span>
                        </div>
                        {isLifetimePlan ? (
                          <div className={'flex gap-3 justify-between text-xs font-light text-thememutedforeground'}>
                            <span>Lifetime Subscription</span>
                          </div>
                        ) : (
                          <div className={'flex gap-3 justify-between text-xs font-light text-thememutedforeground'}>
                            <span>{period === 'monthly' ? 'Monthly' : 'Annual'} Subscription, cancel anytime.</span>
                            <span>{period === 'monthly' ? null : '$42 / mo avg.'}</span>
                          </div>
                        )}
                      </div>

                      <div className={'flex justify-between gap-3'}>
                        <p className={'text-sm'}>Billing Cycle</p>
                        {annualFlagEnabled ? (
                          <div>
                            <p className={'text-sm font-semibold text-right'}>
                              {period === 'monthly' ? 'Monthly' : 'Yearly'}
                            </p>
                            {period === 'annually' && (
                              <p className={'text-xs md:text-sm text-right text-green-600'}>
                                Saving {selectedPlan.amount_saved}/year with Annual Plan
                              </p>
                            )}
                          </div>
                        ) : null}
                      </div>

                      <div className={'flex justify-between gap-3'}>
                        <p className={'text-sm'}>{isLifetimePlan ? null : 'First '}Charge Date</p>
                        <p className={'text-sm font-semibold text-right'}>
                          {isLifetimePlan || isSparkPlan ? firstChargeDate : nextDueDate}
                        </p>
                      </div>

                      {isLifetimePlan ? null : (
                        <div>
                          {addPromoOpen ? (
                            <div className={'flex gap-1.5'}>
                              <FormField
                                control={form.control}
                                name="coupon"
                                render={({ field }) => (
                                  <FormItem className={'flex-1'}>
                                    <FormControl>
                                      <Input type={'text'} placeholder={'Add Promo Code'} {...field} />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <Button
                                onClick={() => {
                                  applyPromo()
                                }}
                                variant={'secondary'}
                                size={'sm'}
                                type={'button'}
                                disabled={isLoadingCoupon || isRefetchingCoupon || form.watch('coupon') === ''}
                                loading={isCouponLoading}
                              >
                                {isLoadingCoupon || isRefetchingCoupon ? 'Checking...' : 'Apply'}
                              </Button>
                            </div>
                          ) : appliedPromo ? (
                            <div>
                              <div className={'flex gap-3 justify-between text-sm'}>
                                <span className={'flex gap-1 items-center'}>
                                  <img src={'/label.png'} alt={''} />
                                  <span>{appliedPromo.name}</span>
                                  <span
                                    onClick={() => {
                                      setCouponRemoveLoading(true)
                                      setAddPromoOpen(false)
                                      setAppliedPromo(undefined)
                                      setAppliedCoupon(undefined)
                                      form.setValue('coupon', '')
                                      setTimeout(() => {
                                        setCouponRemoveLoading(false)
                                      }, 500)
                                    }}
                                    className={'text-thememutedforeground cursor-pointer'}
                                  >
                                    <XCircle className={'w-4 h-4'} />
                                  </span>
                                </span>
                                <span className={'font-semibold text-green-600'}>
                                  -{getFormattedPromoDetails(appliedPromo)}
                                </span>
                              </div>
                              <div
                                className={
                                  'flex gap-3 justify-between text-xs font-light text-thememutedforeground capitalize'
                                }
                              >
                                <span>{getPromoDescription(appliedPromo)}</span>
                                <span>
                                  {appliedPromo.durationType === 'limited_period' &&
                                    `For ${appliedPromo.period} ${appliedPromo.periodUnit}`}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <span
                              onClick={() => setAddPromoOpen(true)}
                              className={'text-sm underline text-themedestructive font-medium cursor-pointer'}
                            >
                              Add Promo Code
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    <div className={'flex justify-between gap-3 py-2'}>
                      <p className={'text-sm'}>Due Today</p>
                      <p className={'text-sm font-semibold text-right'}>
                        {isLifetimePlan || isSparkPlan ? getDiscountedPrice(selectedPlan.price, appliedPromo) : '$0.00'}
                      </p>
                    </div>
                  </div>
                  {extraPaymentMethodsEnabled && (
                    <div className="space-y-3">
                      <div>
                        <p className="font-bold text-center">Checkout With</p>
                      </div>
                      {!isCouponLoading ? (
                        <CheckoutMethods
                          key="checkout-methods"
                          selectedPlan={selectedPlan}
                          coupons={appliedCoupon ? [appliedCoupon] : undefined}
                        />
                      ) : (
                        <div className="flex w-full items-center justify-center">
                          <Loader />
                        </div>
                      )}
                      <div>
                        <p className="text-center">-or-</p>
                      </div>
                    </div>
                  )}
                </div>

                <div>
                  <div className={'flex flex-col gap-6'}>
                    <div>
                      <p className={'font-bold'}>Payment Details</p>
                    </div>
                    <div className={'grid grid-cols-2 gap-3'}>
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <Label>
                              First Name <span className={'text-red-500'}>*</span>
                            </Label>
                            <FormControl>
                              <Input placeholder="First Name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <Label>
                              Last Name <span className={'text-red-500'}>*</span>
                            </Label>
                            <FormControl>
                              <Input placeholder="Last Name" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <CardComponent className="field" ref={cardRef} onReady={onReady}>
                      <div>
                        <Label>
                          Card Number <span className={'text-red-500'}>*</span>
                        </Label>
                        <CardNumber placeholder="4111 1111 1111 1111" className={cn(inputStyles())} />
                      </div>
                      <div className={'grid lg:grid-cols-2 gap-3 mt-4'}>
                        <div>
                          <Label>
                            Expiration Date <span className={'text-red-500'}>*</span>
                          </Label>
                          <CardExpiry placeholder="MM / YY" className={cn(inputStyles())} />
                        </div>
                        <div>
                          <Label>
                            CVV <span className={'text-red-500'}>*</span>
                          </Label>
                          <CardCVV placeholder="CVV" className={cn(inputStyles())} />
                        </div>
                      </div>
                    </CardComponent>
                    <div>
                      <p className={'font-medium'}>Billing Details</p>
                    </div>
                    <div className={'flex flex-col gap-3'}>
                      <FormField
                        control={form.control}
                        name="billingAddress.country"
                        render={({ field }) => (
                          <FormItem>
                            <Label>
                              Country <span className={'text-red-500'}>*</span>
                            </Label>
                            <FormControl>
                              <Select onValueChange={field.onChange} defaultValue={field.value}>
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select country" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {countries &&
                                    countries.map((item) => (
                                      <SelectItem key={item.name} value={item.code2}>
                                        {item.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="billingAddress.line1"
                        render={({ field }) => (
                          <FormItem>
                            <Label>
                              Address <span className={'text-red-500'}>*</span>
                            </Label>
                            <FormControl>
                              <Input placeholder="Line 1" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="billingAddress.line2"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input placeholder="Line 2" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="billingAddress.city"
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input placeholder="City" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <div className={'grid grid-cols-2 gap-3'}>
                        {countries &&
                        form.getValues('billingAddress.country') &&
                        getStatesByCode2(form.watch('billingAddress.country') ?? '').length < 1 ? (
                          <FormField
                            control={form.control}
                            name="billingAddress.state"
                            render={({ field }) => (
                              <FormItem>
                                <FormControl>
                                  <Input placeholder="State" {...field} />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <FormField
                                control={form.control}
                                name="billingAddress.state"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormControl>
                                      <Select
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                        disabled={!form.getValues('billingAddress.country')}
                                      >
                                        <FormControl>
                                          <SelectTrigger>
                                            <SelectValue placeholder="State" />
                                          </SelectTrigger>
                                        </FormControl>
                                        <SelectContent>
                                          {countries &&
                                            form.getValues('billingAddress.country') &&
                                            getStatesByCode2(form.watch('billingAddress.country') ?? '').map((item) => (
                                              <SelectItem key={item.name} value={item.name}>
                                                {item.name}
                                              </SelectItem>
                                            ))}
                                        </SelectContent>
                                      </Select>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </TooltipTrigger>
                            {!form.getValues('billingAddress.country') && (
                              <TooltipContent>
                                <p>Select a country first to enable this</p>
                              </TooltipContent>
                            )}
                          </Tooltip>
                        )}
                        <FormField
                          control={form.control}
                          name="billingAddress.zip"
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input placeholder="Zip code" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </div>
                  </div>
                  <div className={'mt-6'}>
                    <Button disabled={isSubscribing} type={'submit'} className="w-full" loading={isSubscribing}>
                      {getSubmitButtonText({
                        isSubmitting,
                        isSubscribing,
                        isLoadingChargebeeSync,
                        isRefetchingChargebeeSync,
                        isSparkPlan,
                        isLifetimePlan,
                      })}
                    </Button>
                    <div>
                      <span className={'flex justify-center items-center gap-2 text-center mt-1.5 text-sm'}>
                        <Lock className={'w-5'} />
                        <span>Secure Checkout by Chargebee</span>
                      </span>
                    </div>
                  </div>
                  <div className={'mt-6'}>
                    <p className={'text-xs font-light text-thememutedforeground'}>
                      {getCheckoutFooterNote({
                        isLifetimePlan,
                        isSparkPlan,
                        selectedPlan,
                        firstChargeDate,
                        nextDueDate,
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </form>
          </Form>
        )}
      </div>
    </div>
  )
}
